import React, { FormEvent, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocalhost ? 'http://localhost:5000' : '';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialToken = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [token, setToken] = useState(initialToken);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage('');
    setError('');

    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Zurücksetzen fehlgeschlagen');
      }
      setMessage(payload.message || 'Passwort wurde zurückgesetzt.');
      setPassword('');
      setConfirmPassword('');
    } catch (submitError: any) {
      setError(submitError.message || 'Zurücksetzen fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-sky-50 px-6 py-16 text-stone-900">
      <div className="mx-auto max-w-xl rounded-3xl border border-sky-200 bg-white p-8 shadow-sm">
        <p className="text-sm uppercase tracking-[0.35em] text-sky-700">Auth</p>
        <h1 className="mt-3 text-3xl font-semibold">Passwort zurücksetzen</h1>

        <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
          <label className="block">
            <span className="mb-2 block text-sm font-medium">Reset-Token</span>
            <input
              className="w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 outline-none transition focus:border-stone-900"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              required
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium">Neues Passwort</span>
            <input
              className="w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 outline-none transition focus:border-stone-900"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              type="password"
              required
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium">Passwort wiederholen</span>
            <input
              className="w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 outline-none transition focus:border-stone-900"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              type="password"
              required
            />
          </label>

          {message ? <p className="text-sm text-emerald-700">{message}</p> : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          <button
            className="w-full rounded-2xl bg-sky-700 px-4 py-3 text-sm font-medium text-white transition hover:bg-sky-600 disabled:cursor-not-allowed disabled:bg-sky-300"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Speichere...' : 'Passwort setzen'}
          </button>
        </form>
      </div>
    </main>
  );
};

export default ResetPasswordPage;
